// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { mixBarHtml, fuelLegendHtml } from '../components/charts';
import { FUEL_COLOUR, FUEL_LABEL, FUEL_ORDER, type Fuel } from '../fuels';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatCompact, formatNumber, formatPercent } from '../utils/format';

/** Cell shade: interpolate white→fuel colour by the row-share intensity. */
function shade(hex: string, t: number): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const m = (v: number) => Math.round(255 + (v - 255) * Math.min(1, Math.max(0, t)));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}

export function renderTypes(root: HTMLElement, ctx: ViewContext): void {
  const { national } = ctx.data;
  const types = national.byType.filter((t) => t.total > 10000);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Not every vehicle is a car</h1>
      <p class="view-sub">
        The national fuel mix hides wildly different fleets. ${glossaryTerm('passenger', 'Passenger cars')} run on
        petrol; ${glossaryTerm('lcv', 'utes and vans')} and every kind of truck run on diesel; motorcycles are almost
        pure petrol. Electric has barely touched anything that works for a living.
      </p>
    </div>

    <div class="panel" style="margin-bottom:var(--space-xl)">
      <div class="panel-title">Fuel mix by vehicle type</div>
      <div class="panel-sub">Each bar is 100% of that vehicle type. Hover a segment for the exact share and count.</div>
      <div data-role="bars"></div>
      ${fuelLegendHtml(FUEL_ORDER)}
    </div>

    <div class="panel">
      <div class="panel-title">Type × fuel matrix</div>
      <div class="panel-sub">Row-share heatmap — darker means a bigger share of that type. The diesel column lights up for everything except cars and bikes.</div>
      <div class="matrix-scroll" data-role="matrix"></div>
    </div>
  `;

  // Bars.
  const barsSlot = root.querySelector('[data-role="bars"]') as HTMLElement;
  barsSlot.innerHTML = types
    .map(
      (t) => `
      <div style="display:flex;align-items:center;gap:var(--space-md);margin-bottom:10px">
        <div style="width:190px;flex-shrink:0;font-size:var(--font-size-sm);color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">${escapeHtml(t.type)}</div>
        <div style="flex:1;min-width:0">${mixBarHtml(t, FUEL_ORDER)}</div>
        <div style="width:66px;flex-shrink:0;text-align:right;font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-tertiary)">${formatCompact(t.total)}</div>
      </div>`,
    )
    .join('');

  // Matrix.
  const matrixSlot = root.querySelector('[data-role="matrix"]') as HTMLElement;
  const head = `<thead><tr><th>Vehicle type</th>${FUEL_ORDER.map((f) => `<th>${FUEL_LABEL[f]}</th>`).join('')}<th>Total</th></tr></thead>`;
  const body = types
    .map((t) => {
      const cells = FUEL_ORDER.map((f: Fuel) => {
        const pct = t.total > 0 ? (t[f] / t.total) * 100 : 0;
        const bg = shade(FUEL_COLOUR[f], pct / 100);
        const dark = pct > 45;
        const tip = escapeHtml(`${t.type} · ${FUEL_LABEL[f]}: ${formatPercent(pct)} (${formatNumber(t[f])})`);
        return `<td style="background:${bg};color:${dark ? '#fff' : '#0f172a'}" data-tip="${tip}">${pct >= 0.1 ? pct.toFixed(1) : '·'}</td>`;
      }).join('');
      return `<tr><th>${escapeHtml(t.type)}</th>${cells}<td class="t-num" style="color:var(--text-secondary)">${formatCompact(t.total)}</td></tr>`;
    })
    .join('');
  matrixSlot.innerHTML = `<table class="matrix">${head}<tbody>${body}</tbody></table>`;
}
